/**
 * SalesForecast.tsx — Sales / Reports → the Forecast TAB (Phase 2 / 2.3b,
 * F-6, §18 U-1..U-10). One read, GET /v1/sales/forecast for the selected
 * period, rendered as three co-equal parts in this order:
 *
 *   1. ATTRIBUTION (U-4) — a panel ABOVE the grid, equal in weight, stating
 *      both halves (won value that can attain a target and won value that
 *      cannot, each with its count) and the verdict. The verdict is the
 *      server's `attainmentMeasurable`, RENDERED not recomputed (R1): the
 *      threshold lives server-side so two surfaces cannot disagree. It sits
 *      above the grid because on every new tenant — and on Fourth Coffee
 *      today, 99% of won value unattributed — the grid is the footnote and
 *      this panel is the number.
 *   2. ATTAINMENT BY REP (U-5) — the rep grid. A null target reads "No
 *      target set" and a null attainment "—": words, never 0 / 0% / blank.
 *      A real £0 target reads £0.00. Rows exist only for employees with a
 *      quota or a deal in the period; an empty grid is a state, not an error.
 *   3. PIPELINE BY FORECAST CATEGORY (U-6, U-7) — the TENANT-WIDE roll-up,
 *      rendered from `rollup` ALONE and never derived from the rows: a
 *      tenant with no employees (Futured Group) has empty rows and a real
 *      pipeline here. All five buckets always render, zeros included, colour
 *      bound by slug.
 *
 * U-9: excludedNonGbp > 0 renders one quiet line (the nonGbpNote idiom).
 * WON is period-bound; OPEN pipeline is as-of-now (the file header of
 * forecast.go says so, and the roll-up's subtitle repeats it here).
 *
 * 2.3c (P2-11, §19 O-7) — THE OVERRIDE, RENDERED: the rep grid gains an
 * "Overrides" column (count · Σ gap, pairs in the title); the roll-up is the
 * EFFECTIVE view (override category/amount where a row exists) and carries
 * the coverage line — "N of M open deals carry an override (£X of £Y)" —
 * and, when the server says every open deal is overridden (allOverridden,
 * RENDERED not recomputed: the Fourth Coffee shape, 1 of 1), the statement
 * that the effective view IS the override view with the rep view drawn
 * beside it. Every overridden deal is listed with its gap chip ("Rep: Best
 * case → Override: Commit, −£4,000.00"). The attribution panel is UNCHANGED:
 * an override is a forecast of what is open, not a fact about what closed.
 *
 * §33 (W-1..W-7) — THE WON-CELL DRILL. A rep's won cell drills to the deals
 * behind it through the SHARED ReportsDrillView (one component, one
 * reconciliation path — SB-12; relocated to its own file under W-5, never
 * forked), as a NEW kind 'forecastWon' whose every field is copied from
 * this tab's /forecast RESPONSE — employee, wonPence / wonCount /
 * wonExcludedNonGbp and the period — never from the picker's state. The
 * fetch is the server's own predicate for the cell (stage closed_won, the
 * linked employee, closed_at inside the period; the 2.3b F-7 range on GET
 * /v1/sales/deals), and the gate is count-then-pence. NOT a reuse of 'rep'
 * (see reportsDrill.ts): that kind is /reports' performer row and would
 * cross populations silently. The cell is a button ONLY while its figure
 * exists (wonCount + wonExcludedNonGbp > 0, W-4): £0.00 · 0 deals stays
 * text, so an empty row never drills to an empty state. Drill state lives
 * here and the early return sits under the strip, so title and tabs stay
 * (the ReportsBody shape); "Back to forecast" restores the same grid from
 * the same response, no refetch.
 *
 * F2 (Forecast programme, register row 70) — THE HERO above all three:
 * ForecastHero.tsx over forecastHero.ts, spec §3.2 under ADOPTION §18 and the
 * 2026-09-29 rulings (insight rule 1 withheld; targets read attributed won).
 *
 * F3 (register row 71) — SUGGESTED CATEGORIES under the hero:
 * ForecastSuggestions.tsx over forecastSuggestions.ts, spec §3.3 under
 * ADOPTION §18.6 (single Accept only; Accept all REFUSED). After each accept
 * or undo the tab REFETCHES the same period SILENTLY — the last response
 * stays on screen until the new one lands, so the card keeps its "✓ Set"
 * rows and the hero and roll-ups recalculate from the wire, never locally.
 *
 * Still not in this slice (W-8, deferred): the per-rep category breakdown
 * the wire also carries (openByCategory) — a render decision first, and it
 * reads zero on every linked rep of the live tenant today.
 */
import { useEffect, useState } from 'react'
import { LoadingState, ErrorState, EmptyState, Avatar } from '../../DashboardShared'
import { useAsync, fetchSalesForecast } from '../../api'
import { ReportsDrillView } from './ReportsDrillView'
import { type ReportsDrill } from './reportsDrill'
import type { SalesForecast as SalesForecastWire } from '../../../../api/types'
import { money } from '../../../../lib/money'
import { success, warning } from '../../../../theme/tokens'
import { PeriodPicker } from './PeriodPicker'
import { ForecastHero } from './ForecastHero'
import { ForecastSuggestions } from './ForecastSuggestions'
import { type SalesPeriod, currentPeriod, periodLabel } from './salesPeriod'
import {
  targetCell, attainmentCell, attributionSentence, verdictSentence, verdictBadge, forecastNonGbpNote,
  rollupBars, fencedDealCount, overrideColumn, overridePairsLine, overrideCoverageSentence, gapChip,
  wonCellDrill, wonCellDrills,
} from './forecastView'

export function ForecastTab({ onSelectDeal }: { onSelectDeal?: (id: string) => void } = {}) {
  const [period, setPeriod] = useState<SalesPeriod>(() => currentPeriod('quarter'))
  // §33: in-page drill state (the R7-1..6 shape — no URL router). Rendered
  // through the SHARED ReportsDrillView; this tab adds no drill machinery.
  const [drill, setDrill] = useState<ReportsDrill | null>(null)
  const { data, loading, error } = useAsync(
    () => fetchSalesForecast({ periodKind: period.kind, periodStart: period.start }),
    [period.kind, period.start],
  )
  // F3: the silent refetch after a write. `fresh` replaces the first read
  // for the SAME period only; a period change starts from its own read.
  const [fresh, setFresh] = useState<{ key: string; data: SalesForecastWire } | null>(null)
  const key = `${period.kind}:${period.start}`
  useEffect(() => { setFresh(null) }, [key])
  const refetch = () => {
    fetchSalesForecast({ periodKind: period.kind, periodStart: period.start })
      .then(d => setFresh({ key, data: d }))
      .catch(() => { /* the last response stays; the write's own toast has spoken */ })
  }
  const shown = fresh && fresh.key === key ? fresh.data : data
  if (drill) {
    return <ReportsDrillView drill={drill} onBack={() => setDrill(null)} onSelectDeal={onSelectDeal} backLabel="Back to forecast" />
  }
  return (
    <div className="flex flex-col gap-4">
      <PeriodPicker period={period} onChange={setPeriod} />
      {loading && <LoadingState />}
      {!loading && error && <ErrorState message={error} />}
      {!loading && !error && <ForecastView data={shown!} period={period} onDrill={setDrill} onSelectDeal={onSelectDeal} onChanged={refetch} />}
    </div>
  )
}

/** The tab's body from one wire response — pure, so the FG and FC shapes can
 *  be rendered from fixtures. `onDrill` receives the won cell's drill. */
export function ForecastView({ data: f, period, onDrill, onSelectDeal, onChanged }: { data: SalesForecastWire; period: SalesPeriod; onDrill?: (drill: ReportsDrill) => void; onSelectDeal?: (id: string) => void; onChanged?: () => void }) {
  const note = forecastNonGbpNote(f.excludedNonGbp)
  const bars = rollupBars(f.rollup)
  const repBars = rollupBars(f.repRollup)
  const coverage = overrideCoverageSentence(f.overrideCoverage)
  const verdictTone = f.attainmentMeasurable
    ? { background: success.bg, color: success.text }
    : { background: warning.bg, color: warning.text }

  return (
    <>
      {/* F2 (register row 70): the smart forecast hero, spec §3.2 — from the
          F1 keys alone; renders nothing on a response that predates F1. */}
      <ForecastHero data={f} onSelectDeal={onSelectDeal} />

      {/* F3 (register row 71): suggested categories, spec §3.3 — the
          server's suggestions, one Accept per deal; no Accept all. */}
      <ForecastSuggestions data={f} onChanged={onChanged} onSelectDeal={onSelectDeal} />

      {/* 1. Attribution — U-4, above the grid, equal weight. */}
      <section className="k-card" aria-label="Attribution">
        <div className="p-5 flex flex-col gap-4">
          <div className="flex items-start justify-between gap-4">
            <div>
              <div className="text-13 font-semibold text-ink-primary">Attribution</div>
              <div className="text-12 text-ink-muted mt-1">
                Won value counts against a target only when its deal is linked to an employee. A won deal owned by a name alone counts for nobody's target.
              </div>
            </div>
            <span className="k-badge" style={verdictTone} data-measurable={f.attainmentMeasurable}>
              <span className="k-badge-dot" />{verdictBadge(f.attainmentMeasurable)}
            </span>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <div>
              <div className="k-metric-label uppercase">Unattributed won</div>
              <div className="text-section-title font-semibold text-ink-primary leading-tight mt-1">{money(f.unattributedWonPence / 100)}</div>
              <div className="text-12 text-ink-muted mt-1">{fencedDealCount(f.unattributedWonCount, f.unattributedWonExcludedNonGbp)} owned by a name, not a person</div>
            </div>
            <div>
              <div className="k-metric-label uppercase">Attributed won</div>
              <div className="text-section-title font-semibold text-ink-primary leading-tight mt-1">{money(f.attributedWonPence / 100)}</div>
              <div className="text-12 text-ink-muted mt-1">{fencedDealCount(f.attributedWonCount, f.attributedWonExcludedNonGbp)} linked to an employee</div>
            </div>
          </div>
          <p className="text-13 text-ink-body">
            {attributionSentence(f)}{' '}
            <span className="font-semibold text-ink-primary">{verdictSentence(f.attainmentMeasurable)}</span>
          </p>
        </div>
      </section>

      {/* 2. The rep grid — U-5. */}
      <section className="k-card" aria-label="Attainment by rep">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">Attainment by rep</div>
            <div className="k-card-sub">{`Won value against target, ${periodLabel(period)} · open pipeline as it stands today`}</div>
          </div>
          {f.totalTargetPence !== null && (
            <div className="text-12 text-ink-muted">Total target {money(f.totalTargetPence / 100)}</div>
          )}
        </div>
        {note && (
          <div className="px-5 py-2 text-12 text-ink-muted border-b border-edge-row">{note}</div>
        )}
        {f.rows.length === 0 ? (
          <EmptyState message="No one to measure yet"
            hint="A row appears for each employee with a target or a won deal in this period. Set targets under Settings → Sales, and link deals to employees so their wins count." />
        ) : (
          <table className="closr-table sales-table w-full">
            <thead>
              <tr>
                <th className="text-left">Rep</th>
                <th className="text-left">Target</th>
                {/* §26 F-2a: the population is NAMED — linked employees (a
                    target needs one); what nobody is linked to is the
                    attribution panel above, never a grid row. */}
                <th className="text-left" title="Linked employees; unattributed value is stated above">Won</th>
                <th className="text-left">Attainment</th>
                <th className="text-left">Open pipeline</th>
                <th className="text-left">Weighted</th>
                <th className="text-left">Overrides</th>
              </tr>
            </thead>
            <tbody>
              {f.rows.map(r => (
                <tr key={r.employeeId}>
                  <td>
                    <div className="flex items-center gap-2 text-13 font-medium text-ink-primary">
                      <Avatar name={r.employeeName} size={22} />{r.employeeName}
                    </div>
                  </td>
                  <td className={r.targetPence === null ? 'text-13 text-ink-muted' : 'text-13 font-semibold text-ink-primary'}>
                    {targetCell(r.targetPence)}
                  </td>
                  <td className="text-13 text-ink-body">
                    {/* §33 W-4: a button only while the figure exists; the
                        resting classes are the cell's own, so a drillable
                        cell and a text cell look the same at rest. */}
                    {wonCellDrills(r) ? (
                      <button type="button" data-drill="won" title="Show the deals behind this figure"
                        onClick={() => onDrill?.(wonCellDrill(f, r))}
                        className="text-left cursor-pointer rounded-control hover:underline focus:outline-none">
                        {money(r.wonPence / 100)} <span className="text-12 text-ink-muted">· {fencedDealCount(r.wonCount, r.wonExcludedNonGbp)}</span>
                      </button>
                    ) : (
                      <>{money(r.wonPence / 100)} <span className="text-12 text-ink-muted">· {fencedDealCount(r.wonCount, r.wonExcludedNonGbp)}</span></>
                    )}
                  </td>
                  <td className={r.attainmentPct === null ? 'text-13 text-ink-muted' : 'text-13 font-semibold text-ink-primary'}>
                    {attainmentCell(r.attainmentPct)}
                  </td>
                  <td className="text-13 text-ink-body">
                    {money(r.openPence / 100)} <span className="text-12 text-ink-muted">· {fencedDealCount(r.openCount, r.openExcludedNonGbp)}</span>
                  </td>
                  <td className="text-13 text-ink-body">{money(r.weightedPence / 100)}</td>
                  {/* 2.3c: count · Σ gap; the (rep → override) pairs ride the title. */}
                  <td className={r.overriddenCount === 0 ? 'text-13 text-ink-muted' : 'text-13 text-ink-body'}
                    title={r.overriddenCount ? overridePairsLine(r.overridePairs) : undefined} data-overridden={r.overriddenCount}>
                    {overrideColumn(r.overriddenCount, r.overrideGapPence)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>

      {/* 3. The tenant-wide roll-up — U-6 (from `rollup` alone), U-7 (all
          five buckets, fixed slots). The Pipeline-funnel idiom: a label, a
          bar against the largest bucket, the figures. */}
      <section className="k-card" aria-label="Pipeline by forecast category">
        <div className="k-card-head">
          <div>
            <div className="k-card-title">Pipeline by forecast category</div>
            <div className="k-card-sub">Every open deal in the workspace as it stands today, by effective category — whoever owns it</div>
          </div>
        </div>
        {/* 2.3c: the coverage line, above the bars so the reader knows WHOSE
            view the bars are before reading them. data-all-overridden is
            the server's verdict, rendered. */}
        {coverage && (
          <div className="px-5 py-2 text-12 text-ink-body border-b border-edge-row" data-all-overridden={f.overrideCoverage.allOverridden}>
            {coverage}
          </div>
        )}
        <div className="p-4 flex flex-col gap-3" data-view="effective">
          {f.overrideCoverage.allOverridden && (
            <div className="text-11 font-semibold uppercase text-ink-muted">Effective view — the override view</div>
          )}
          {bars.map(b => <RollupRow key={b.category} bar={b} />)}
        </div>
        {/* At N = M the rep view is drawn BESIDE the effective one, so one
            person's second opinion is never presented as the pipeline. */}
        {f.overrideCoverage.allOverridden && (
          <section className="p-4 pt-0 flex flex-col gap-3 border-t border-edge-row" data-view="rep" aria-label="Rep view">
            <div className="text-11 font-semibold uppercase text-ink-muted mt-4">Rep view — the deals' own categories and values</div>
            {repBars.map(b => <RollupRow key={b.category} bar={b} />)}
          </section>
        )}
        {/* Every overridden open deal, with its gap chip (§17.4). */}
        {f.overrides.length > 0 && (
          <ul className="border-t border-edge-row divide-y divide-edge-row" aria-label="Overridden deals">
            {f.overrides.map(o => (
              <li key={o.dealId} className="px-5 py-2 flex items-center justify-between gap-4 text-12" data-deal-id={o.dealId}>
                <span className="text-ink-primary font-medium truncate">
                  {o.title}{o.employeeName ? <span className="text-ink-muted font-normal"> · {o.employeeName}</span> : null}
                </span>
                <span className="k-badge flex-shrink-0" data-gap-chip>{gapChip(o.repCategory, o.overrideCategory, o.gapPence, o.amountPence !== null)}</span>
              </li>
            ))}
          </ul>
        )}
      </section>
    </>
  )
}

function RollupRow({ bar: b }: { bar: ReturnType<typeof rollupBars>[number] }) {
  return (
    <div className="flex items-center gap-3" data-category={b.category}>
      <div className="w-28 flex-shrink-0 text-12 text-ink-secondary">{b.label}</div>
      <div className="flex-1 h-2 rounded-full bg-surface-control overflow-hidden">
        <div className="h-full rounded-full" style={{ width: `${b.widthPct}%`, background: b.fill }} />
      </div>
      <div className="w-28 flex-shrink-0 text-right text-13 font-semibold text-ink-primary">{money(b.pence / 100)}</div>
      <div className="w-40 flex-shrink-0 text-right text-12 text-ink-muted">{money(b.weightedPence / 100)} weighted</div>
      <div className="w-16 flex-shrink-0 text-right text-12 text-ink-muted">{fencedDealCount(b.count, b.excludedNonGbp)}</div>
    </div>
  )
}
